import type { Language } from '@offer-catalog/services/i18n'
import type { HomologationStatus, ProductHomologationByStatus } from '@/components/ProductCard/types'
import {
  VpAccordion,
  VpAccordionDivider,
  VpAccordionItem,
  VpAccordionItemHeader,
  VpAccordionItemHeaderIcon,
  VpAccordionItemHeaderLabel,
  VpAccordionItemPanel,
  VpBadge,
} from '@vtmn-play/react'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { Modal } from '@/components/Modal'
import { HOMOLOGATION_STATUS_CONFIG } from '@/constants/homologation'
import { useCountriesContext } from '@/contexts/countries/useCountriesContext'
import { getCountryName } from '@/utils/countries'
import { HOMOLOGATION_STATUS_COLOR } from './utils'
import './homologation.scss'

export interface GroupedHomologation {
  status: HomologationStatus
  countries: {
    countryCode: string
    lastUpdateDateTime: string
  }[]
}

export interface HomologationModalProps {
  homologatedCountries: ProductHomologationByStatus
  openModal: boolean
  validate: () => void
  setOpenModal: (status: boolean) => void
}

export function HomologationModal({
  homologatedCountries,
  openModal,
  validate,
  setOpenModal,
}: Readonly<HomologationModalProps>) {
  const { i18n, t } = useTranslation()
  const { getCountryByCode, normalizeCountryCodes } = useCountriesContext()
  const [searchParams] = useSearchParams()
  const [openPanel, setOpenPanel] = useState<string[]>([])
  const currentLanguage = i18n.language.split('-')[0] as Language
  const selectedCountryCodes = normalizeCountryCodes(searchParams.getAll('homologation_countries'))
  const selectedCountry = selectedCountryCodes.length === 1
    ? getCountryByCode(selectedCountryCodes[0])
    : null
  const selectedCountryCodeSet = new Set(selectedCountryCodes)

  const visibleGroups = HOMOLOGATION_STATUS_CONFIG
    .map(({ group, status, translationKey }) => ({
      group,
      status,
      translationKey,
      countries: homologatedCountries[group].filter(country => selectedCountryCodeSet.size > 0
        ? selectedCountryCodeSet.has(country.country_code)
        : true),
    }))
    .filter(group => group.countries.length > 0)
  const statuses = visibleGroups.map(group => group.status)

  return (
    <Modal
      dataTestid="homologation-modal"
      isOpen={openModal}
      title={t('products-page.product-card.homologation-modal-title')}
      width="60vw"
      validationAction={() => {
        validate()
        setOpenModal(false)
      }}
      onClose={() => setOpenModal(false)}
    >
      <div className="modal-homologation-by-status" data-testid="modal-homologation-by-status">
        <VpAccordion
          value={selectedCountry ? statuses : openPanel}
          onChange={(e: string[]) => setOpenPanel(e)}
        >
          {visibleGroups.map(({ status, translationKey, countries }) => (
            <Fragment key={status}>
              <VpAccordionItem value={status}>
                <VpAccordionItemHeader>
                  <VpAccordionItemHeaderLabel>
                    <span style={{ color: HOMOLOGATION_STATUS_COLOR[status as HomologationStatus] }}>
                      {t(`products-page.product-card.homologation_statuses.${translationKey}`)}
                    </span>
                    {!selectedCountry && <VpBadge>{countries.length}</VpBadge>}
                  </VpAccordionItemHeaderLabel>
                  {!selectedCountry && <VpAccordionItemHeaderIcon />}
                </VpAccordionItemHeader>
                <VpAccordionItemPanel>
                  {countries.map(country => (
                    <div key={country.country_code} className="modal-homologation-country-and-comment">
                      {!selectedCountry && <div className="modal-homologation-country">{getCountryName(country.country_code, currentLanguage)}</div>}
                      {'specific_status' in country && country.specific_status && (
                        <div className="modal-homologation-specific-status">{country.specific_status.toUpperCase()}</div>
                      )}
                      <div className="modal-homologation-comment">{country.status_comment}</div>
                    </div>
                  ))}
                </VpAccordionItemPanel>
              </VpAccordionItem>
              {!selectedCountry && <VpAccordionDivider />}
            </Fragment>
          ))}
        </VpAccordion>
      </div>
    </Modal>
  )
}
